import { types, destroy, cast, SnapshotIn, onPatch } from "mobx-state-tree"

import { MapEntityModel, MapEntity, Polygon, Point, PointType, MapEntityAttributes, MapEntityStatus, Launchpad, isPointType, isPolygonType, PointModel, PolygonModel } from "models/FlightNetwork/MapEntity";
import { FilterType } from "models/common/Filters/FilterType";
import { SearchFieldFilterModel } from "models/common/Filters/SearchFieldFilterModel";
import { FlightNetworkStatus } from "services/oversight/types";

export enum ReviewStatus {
  Approved = 'approved',
  Unapproved = 'unapproved',
}

const SEARCH_FILTER: SnapshotIn<typeof SearchFieldFilterModel> = {
  type: FilterType.SEARCH,
  placeholder: 'Search',
}

export const isTypeFilter = (filter: string): boolean => {
  return isPointType(filter) || isPolygonType(filter)
}

export const isStatusFilter = (filter: string): boolean => {
  return Object.values(MapEntityStatus).some(status => status === filter)
}

export const isReviewStatusFilter = (filter: string): boolean => {
  return Object.values(ReviewStatus).some(status => status === filter)
}

export const MapEntitiesStore = types.model('MapEntitiesStore')
  .props({
    flightNetworkStatus: types.maybeNull(types.enumeration('FlightNetworkStatus', Object.values(FlightNetworkStatus))),
    entities: types.array(MapEntityModel),
    selectedEntity: types.maybeNull(types.reference(MapEntityModel)),
    selectedEntitySelectedAt: types.maybeNull(types.Date),
    searchFilter: types.optional(SearchFieldFilterModel, SEARCH_FILTER),
    filters: types.optional(types.array(types.string), []),
    filteredEntities: types.map(types.reference(MapEntityModel)),
    selectedPolygonVertex: types.maybeNull(types.number),
  })
  .views((self) => ({
    get selectedVertex(): number | null {
      return self.selectedPolygonVertex
    },
    get filteredSortedEntities(): MapEntity[] {
      return sortEntities([...self.filteredEntities.values()])
    },
    get preparedFilters(): preparedFilters {
      const searchTerm = self.searchFilter?.value?.toLocaleLowerCase() ?? ''
      const typeFilters = self.filters.filter(isTypeFilter)
      const statusFilters = self.filters.filter(isStatusFilter)
      const reviewStatusFilters = self.filters.filter(isReviewStatusFilter)

      return {
        searchTerm,
        typeFilters,
        statusFilters,
        reviewStatusFilters,
      }
    }
  }))
  .actions((self) => ({
    runFiltersForEntity(entity: MapEntity) {
      const passedFilters = doesEntityPassFilters(entity, self.preparedFilters)
      
      if (passedFilters) {
        self.filteredEntities.put(entity)
      } else {
        self.filteredEntities.delete(entity.uuid)
      }
    },
    runFilters() {
      self.filteredEntities.clear()

      const filtersPresent = Object.entries(self.preparedFilters).some((_, filter) => Boolean(filter))

      if (!filtersPresent) {
        self.entities.forEach(entity => self.filteredEntities.put(entity))
        return
      }

      const filtered = self.entities.filter((e) => doesEntityPassFilters(e, self.preparedFilters))

      filtered.forEach(entity => self.filteredEntities.put(entity))
    }
  }))
  .actions((self) => ({
    setFlightNetworkStatus(status: FlightNetworkStatus) {
      self.flightNetworkStatus = status
    },
    setEntities(entities: MapEntityAttributes[]) {
      /*
        Clear entities array to avoid heavy MST reconciliation
        Read more: https://github.com/mobxjs/mobx-state-tree/issues/2237
      */
      self.entities = cast([])
      self.entities = cast(entities)
      
      self.runFilters()
      self.entities.forEach((entity) => {
        onPatch(entity, () => {
          self.runFiltersForEntity(entity)
        })
      })
    },
    addEntity(entity: MapEntity) {
      self.entities.push(entity)

      self.runFiltersForEntity(entity)

      onPatch(entity, () => {
        self.runFiltersForEntity(entity)
      })
    },
    removeEntity(entity: MapEntity) {
      self.filteredEntities.delete(entity.uuid)
      destroy(entity)
    },
    setSelectedEntity(entity: MapEntity | null) {
      self.selectedEntity = entity
      self.selectedEntitySelectedAt = entity ? new Date() : null
    },
    setSelectedVertex(vertex: number | null) {
      self.selectedPolygonVertex = vertex
    },
    approveAll() {
      self.entities.forEach((entity) => {
        if (entity.requiresReview) {
          entity.setIsReviewed(true)
        }
      })
    },
    setSearchFilter(value: string) {
      self.searchFilter.setValue(value)
      self.runFilters()
    },
    setFilters(value: string[]) {
      self.filters = cast(value)
      self.runFilters()
    },
    clearFilters() {
      self.filters = cast([])
      self.searchFilter.setValue(null)
      self.runFilters()
    }
  }))
  .actions((self) => ({
    markAsInvalid(uuid: string): void {
      const target = self.entities.find(entity => entity.uuid === uuid)
    
      if (!target) {
        throw new Error('Invalid entity not found')
      }
      
      target.setHasErrors(true)
      self.setSelectedEntity(target)
    },
  }))
  .views((self) => ({
    get polygons(): Polygon[] {
      return self.entities.filter((e) => PolygonModel.is(e)) as Polygon[]
    },
    get points(): Point[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type !== PointType.Center) as Point[]
    },
    get launchpads(): Launchpad[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type === PointType.Launchpad) as Launchpad[]
    },
    get deliveries(): Point[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type === PointType.Delivery) as Point[]
    },
    get safeLandings(): Point[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type === PointType.SafeLanding) as Point[]
    },
    get pickups(): Point[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type === PointType.Pickup) as Point[]
    },
      get docks(): Point[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type === PointType.Dock) as Point[]
    },
    /*
      In most cases, there is only one center as the user can't add or delete it.
      However, when importing a flight network dump, there are gonna be two centers.
    */
    get centers(): Point[] {
      return self.entities.filter((e) => PointModel.is(e) && e.type === PointType.Center) as Point[]
    },
    get hasReviewedChanges(): boolean {
      return self.entities.some((entity) => {
        return entity.isReviewed
      })
    },
    get hasUnreviewedChanges(): boolean {
      return self.entities.some((entity) => {
        const hasChange = entity.isNew || entity.isDeleted || entity.isModified

        return hasChange && entity.requiresReview
      })
    },
  }))
  .views((self) => ({
    get deliveriesAndLaunchpads(): Point[] {
      return self.deliveries.concat(self.launchpads)
    },
  }))
  .views((self) => ({
    get activeFilters(): string[] {
      return self.searchFilter?.value ? [...self.filters, 'search'] : self.filters
    },
    get filteredEntityUuids(): Set<string> {
      return new Set(self.filteredSortedEntities.map(e => e.uuid))
    },
  }))


type preparedFilters = {
  searchTerm: string, 
  typeFilters: string[], 
  statusFilters: string[], 
  reviewStatusFilters: string[]
}

const doesEntityPassFilters = (entity: MapEntity, preparedFilters: preparedFilters): boolean => {
  const { searchTerm, typeFilters, statusFilters, reviewStatusFilters } = preparedFilters

  let satisfiedFilters = true
  if (searchTerm.length > 0) {
    satisfiedFilters &&= 
      (entity.name?.toLocaleLowerCase().includes(searchTerm) || 
        entity.uuid.toLocaleLowerCase().includes(searchTerm))
  }
  if (typeFilters.length > 0) {
    satisfiedFilters &&= typeFilters.includes(entity.type) 
  }
  if (statusFilters.length > 0) {
    satisfiedFilters &&= statusFilters.includes(entity.status) 
  }
  if (reviewStatusFilters.length > 0) {
    const approvalStatus = entity.isReviewed ? ReviewStatus.Approved : 
      entity.requiresReview ? ReviewStatus.Unapproved : null
    satisfiedFilters &&= (approvalStatus !== null  && reviewStatusFilters.includes(approvalStatus))
  }

  return satisfiedFilters
}

const sortEntities = (entities: MapEntity[]): MapEntity[] => {
  return entities.sort((a: MapEntity, b: MapEntity) => {
    if (a.created_at === null) return 1
    if (b.created_at === null) return -1

    return Number(b.created_at) - Number(a.created_at)
  })
}